import { useEffect, useRef, useState } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import api, { dedupedRequest } from '../../utils/axios';

const ROLE_OPTIONS = ['superadmin', 'admin', 'client'];

type Props = {
  show: boolean;
  handleClose: () => void;
  map: { id: number; name: string; svg_content: string };
};

const ProcessMapModal = ({ show, handleClose, map }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const [previousSelected, setPreviousSelected] = useState<SVGElement | null>(null);

  const [linkType, setLinkType] = useState<'map' | 'documents'>('documents');
  const [targetMapId, setTargetMapId] = useState<number | ''>('');  // dropdown or number input
  const [targetDocumentTag, setTargetDocumentTag] = useState<string>('');

  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [existingLinkId, setExistingLinkId] = useState<number | null>(null); // optional

  const handleSaveLink = () => {
    if (!selectedElementId || !map?.id) return;

    api.post('/admin/process_links', {
      process_map_id: map.id,
      element_id: selectedElementId,
      link_type: linkType,
      target_map_id: linkType === 'map' ? Number(targetMapId) : null,
      target_document_tag: linkType === 'documents' ? targetDocumentTag : null,
      roles: selectedRoles,
    })
    .then(() => {
      alert('Link saved successfully.');
    })
    .catch((err) => {
      console.error(err);
      alert('Failed to save link.');
    });
  };

  const handleDeleteLink = () => {
    if (!selectedElementId || !map?.id) return;

    api.delete(`/admin/process_links?map_id=${map.id}&element_id=${selectedElementId}`)
    .then(() => {
      alert('Link deleted successfully.');
      setLinkType('documents');
      setTargetMapId('');
      setTargetDocumentTag('');
      setSelectedRoles([]);
    })
    .catch((err) => {
      console.error(err);
      alert('Failed to delete link.');
    });
  };

  useEffect(() => {
    if (!show || !map.svg_content) return;

    const container = containerRef.current;
    if (!container) return;

    container.innerHTML = map.svg_content;

    const anchors = container.querySelectorAll('a[id]');
    anchors.forEach((anchor) => {
      anchor.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const el = e.currentTarget as SVGElement;
        const id = el.getAttribute('id');
        if (previousSelected) {
            previousSelected.classList.remove('selected-map-element');
        }

        el.classList.add('selected-map-element');

        setPreviousSelected(el);
        setSelectedElementId(id);
      });
    });

    return () => {
      anchors.forEach((anchor) => {
        anchor.removeEventListener('click', () => {});
      });
    };
  }, [show, map, previousSelected]);

  useEffect(() => {
    // Clean up selection when modal closes
    if (!show && previousSelected) {
      previousSelected.classList.remove('selected-map-element');
      setPreviousSelected(null);
      setSelectedElementId(null);
    }
  }, [show]);

  useEffect(() => {
  if (selectedElementId && map.id) {
    dedupedRequest({ method: 'GET', url: `/admin/process_links?map_id=${map.id}&element_id=${selectedElementId}` }, { useCache: true })
    .then((res) => {
      const data = res.data;
      if (data) {
        setLinkType(data.link_type);
        setTargetMapId(data.target_map_id || '');
        setTargetDocumentTag(data.target_document_tag || '');
        setSelectedRoles(data.roles || []);
        setExistingLinkId(1);
      } else {
        // Clear previous data if this is a new link
        setLinkType('documents');
        setTargetMapId('');
        setTargetDocumentTag('');
        setSelectedRoles([]);
        setExistingLinkId(null);
      }
    })
    .catch(err => console.error('Failed to load link data:', err));

  }
}, [selectedElementId]);


  return (
    <Modal show={show} onHide={handleClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Preview: {map.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div ref={containerRef} />
        {selectedElementId && (
          <Form className="mt-4 border-top pt-3">
            <h5>Edit Link: <code>{selectedElementId}</code></h5>
      <Row>
        <Col>
            <Form.Group controlId="linkType">
              <Form.Label>Link Type</Form.Label>
              <Form.Select
                value={linkType}
                onChange={(e) => setLinkType(e.target.value as 'map' | 'documents')}
              >
                <option value="documents">Documents</option>
                <option value="map">Another Process Map</option>
              </Form.Select>
            </Form.Group>
        </Col>
        <Col xs={8}>
            {linkType === 'map' && (
              <Form.Group controlId="targetMapId">
                <Form.Label>Target Map ID</Form.Label>
                <Form.Control
                  type="number"
                  value={targetMapId}
                  onChange={(e) => setTargetMapId(e.target.value === '' ? '' : Number(e.target.value))}
                />
              </Form.Group>
            )}
            {linkType === 'documents' && (
              <Form.Group controlId="targetDocumentTag">
                <Form.Label>Target Document Tag</Form.Label>
                <Form.Control
                  type="text"
                  value={targetDocumentTag}
                  onChange={(e) => setTargetDocumentTag(e.target.value)}
                />
              </Form.Group>
            )}
            </Col>
            </Row>
            <Form.Group controlId="roleCheckboxGroup">
              <Form.Label>Allowed Roles</Form.Label>
              <div>
                {ROLE_OPTIONS.map((role) => (
                  <Form.Check
                    key={role}
                    inline
                    type="switch"
                    id={`checkbox-${role}`}
                    label={role}
                    value={role}
                    checked={selectedRoles.includes(role)}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const value = e.target.value;
                      if (checked) {
                        setSelectedRoles([...selectedRoles, value]);
                      } else {
                        setSelectedRoles(selectedRoles.filter(r => r !== value));
                      }
                    }}
                  />
                ))}
              </div>
            </Form.Group>

            <Button className="mt-3 me-2" onClick={handleSaveLink}>
              Save
            </Button>

            {existingLinkId && (
              <Button variant="danger" className="mt-3" onClick={handleDeleteLink}>
                Delete
              </Button>
            )}
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ProcessMapModal;
